// The app mirrors its navigation state (page, open detail and its tab, report
// date range and work type) into query parameters —
// `?view=projects&project=<id>&tab=memory&from=...&to=...&work=client` —
// so the browser Back button, refresh and shared links all work. Query params
// (not paths) on purpose: the SPA is deployed under a subpath (e.g.
// /time-tracker/) and `api()` builds URLs relative to the current path, so
// path-based routes would break API calls and need an nginx history fallback.
import { isoDate, parseIsoDate } from './time.js';
import { normalizeWorkFilter } from './workTypes.js';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// The report work type filter (workTypes.js#normalizeWorkFilter); 'all' is
// the default and never written to the URL.

// Only one detail param is meaningful at a time — it must match the view.
export const DETAIL_PARAM_BY_VIEW = {
  team: 'user',
  projects: 'project',
  clients: 'client',
};

// The tabs of the details that have them, the default first. The tab belongs
// to the open record, so it lives in the URL next to the detail id: Back,
// refresh and a shared link restore it, and opening another record starts on
// the default. The default is never written to the URL.
export const DETAIL_TABS = {
  projects: ['info', 'tracker', 'history', 'memory'],
  clients: ['info', 'effort', 'outlook', 'transactions', 'billing'],
};

// `tab` when it is a non-default tab of the view's detail, else null.
function detailTab(view, tab) {
  const tabs = DETAIL_TABS[view];
  return tabs && tab !== tabs[0] && tabs.includes(tab) ? tab : null;
}

// Shape alone is not enough: "2026-02-31" matches the regex but JS Date would
// silently normalize it to another day and Postgres would reject it outright.
// Round-tripping through parse + format only accepts dates that exist.
function validDate(value) {
  if (!value || !ISO_DATE_RE.test(value)) return null;
  return isoDate(parseIsoDate(value)) === value ? value : null;
}

// Parse a location.search string into the navigation state it encodes.
// Unknown params are ignored; malformed dates are dropped so a hand-edited
// URL degrades to the defaults instead of producing a broken report query.
// The dates are an atomic, ordered pair: a lone or inverted endpoint would
// combine with a default into an interval that silently matches nothing, so
// anything short of a complete valid range yields no range at all.
export function readUrlState(search) {
  const params = new URLSearchParams(search);
  const view = params.get('view') || null;
  const detailParam = DETAIL_PARAM_BY_VIEW[view];
  const from = validDate(params.get('from'));
  const to = validDate(params.get('to'));
  const hasRange = Boolean(from && to && from <= to);
  const work = params.get('work');
  const detailId = detailParam ? params.get(detailParam) || null : null;
  return {
    view,
    detailId,
    tab: detailId ? detailTab(view, params.get('tab')) : null,
    from: hasRange ? from : null,
    to: hasRange ? to : null,
    billing: work ? normalizeWorkFilter(work) : null,
  };
}

// True when this transition pushes a detail on top of its own list — the one
// case where the history entry beneath the detail is exactly the screen its
// in-app "All ..." control closes back to.
export function openedDetailFromList(previous, next) {
  return Boolean(
    previous &&
    previous.view === next.view &&
    !previous.detailId &&
    next.detailId,
  );
}

// Which history action a navigation change deserves, given where we were.
// Opening a page or a detail pushes, so Back walks the screens you visited.
// A date-range tweak replaces (Back is not an undo stack for the filter).
// Closing a detail with its own "All ..." control depends on how the detail
// got here: when it was pushed from its own list (`detailCameFromList`), walk
// back to that entry — replacing would stack a second identical list entry
// and make the next Back a no-op; otherwise (deep link, arrival from another
// page) replace, so Back never reopens the detail the user just closed.
// `previous` is null on the very first render, where the loaded URL is
// normalized in place.
export function historyMode(previous, next, detailCameFromList = false) {
  if (!previous) return 'replace';
  const movedScreen =
    previous.view !== next.view || previous.detailId !== next.detailId;
  if (!movedScreen) return 'replace';
  const closedDetail =
    previous.view === next.view && previous.detailId && !next.detailId;
  if (closedDetail) return detailCameFromList ? 'back' : 'replace';
  return 'push';
}

// The work type a Back/Forward pop restores. An entry without `work` was
// written for the default 'all' (buildSearch never writes it), so it means
// 'all', not "keep the current type"; only a pop started by closing a detail
// in-app (`keepRange`) keeps the current one, like the date range.
export function poppedBilling(state, current, keepRange = false) {
  if (keepRange) return current;
  return state.billing || 'all';
}

// The report filter params (date range and work type). Unlike the page and
// detail, these belong to whoever set them: logout and a change of account
// on the tab drop them so the next person starts from the defaults.
const REPORT_FILTER_PARAMS = ['from', 'to', 'work'];

// `search` without the report filter params, other params kept as they were.
export function withoutReportFilters(search) {
  const params = new URLSearchParams(search);
  REPORT_FILTER_PARAMS.forEach((name) => params.delete(name));
  const next = params.toString();
  return next ? `?${next}` : '';
}

// Serialize navigation state back into a search string ("" when empty).
// Built from scratch: params this module doesn't own (e.g. a stale ?outlook=
// from the OAuth callback) are intentionally not carried along.
export function buildSearch({ view, detailId, tab, from, to, billing }) {
  const params = new URLSearchParams();
  if (view) params.set('view', view);
  const detailParam = DETAIL_PARAM_BY_VIEW[view];
  if (detailParam && detailId) {
    params.set(detailParam, detailId);
    if (detailTab(view, tab)) params.set('tab', tab);
  }
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const work = billing && normalizeWorkFilter(billing);
  if (work && work !== 'all') params.set('work', work);
  const search = params.toString();
  return search ? `?${search}` : '';
}
